// CustomDropdown.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    View,
    Text,
    TouchableOpacity,
    FlatList,
    TextInput,
    StyleSheet,
    Platform,
    Pressable,
    Dimensions,
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
const { width: windowWidth, height: windowHeight } = Dimensions.get('window');
const IS_WEB = Platform.OS === 'web';
const IS_WEB_WIDE = Platform.OS === 'web' && windowWidth >= 600;
const IS_DESKTOP_FORM = Platform.OS === 'web' && windowWidth >= 1024;
const IS_TABLET_FORM = Platform.OS === 'web' && windowWidth >= 600 && windowWidth < 1024;
// ─── Web Portal ────────────────────────────────────────────────────────────────
// ReactDOM.createPortal keeps the component inside the SAME React tree,
// so state updates propagate normally — no stale closure problem.
// We render the portal target div once and reuse it.

let ReactDOM = null;
let createPortal = null;
if (Platform.OS === 'web') {
    ReactDOM = require('react-dom');
    createPortal = ReactDOM.createPortal;
}

// Singleton portal container attached to document.body
let portalRoot = null;
const getPortalRoot = () => {
    if (!portalRoot && typeof document !== 'undefined') {
        portalRoot = document.createElement('div');
        portalRoot.setAttribute('id', 'custom-dropdown-portal');
        document.body.appendChild(portalRoot);
    }
    return portalRoot;
};

// ─── Main Component ────────────────────────────────────────────────────────────

const CustomDropdown = ({
    data = [],
    selectedValue,
    onSelect,
    placeholder = 'নির্বাচন করুন',
    placeholderTextColor = '#989898',
    selectedTextColor = '#333333',
    containerStyle = {},
    dropdownButtonStyle = {},
    dropdownListStyle = {},
    itemStyle = {},
    itemTextStyle = {},
    searchPlaceholder = 'সার্চ করুন...',
    showSearch = null,
    maxHeight = 300,
    zIndex = 1000,
    selectOptionsWidth = 250,
    onOpenChange,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const containerRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
        width: selectOptionsWidth,
        openUpward: false,
        bottom: 0,
    });

    const shouldShowSearch = showSearch !== null ? showSearch : data.length > 10;

    const filteredData = searchText.trim()
        ? data.filter(item =>
            String(item.label || '').toLowerCase().includes(searchText.toLowerCase()),
        )
        : data;

    // ── Calculate fixed position from button rect ──────────────────────────────
    const updateDropdownPosition = useCallback(() => {
        if (!containerRef.current || Platform.OS !== 'web') return;
        const rect = containerRef.current.getBoundingClientRect();
        const dropWidth = Math.max(rect.width, selectOptionsWidth);
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUpward = spaceBelow < maxHeight && rect.top > maxHeight;
        setDropdownPosition({
            top: rect.bottom + 2,
            bottom: window.innerHeight - rect.top + 2,
            left: rect.left,
            width: dropWidth,
            openUpward,
        });
    }, [selectOptionsWidth, maxHeight]);

    // ── Click-outside to close (web only) ─────────────────────────────────────
    useEffect(() => {
        if (Platform.OS !== 'web' || !isOpen) return;

        const handleClickOutside = event => {
            const inButton =
                containerRef.current && containerRef.current.contains(event.target);
            const portalEl = getPortalRoot();
            const inPortal = portalEl && portalEl.contains(event.target);
            if (!inButton && !inPortal) {
                closeDropdown();
            }
        };

        document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Reposition on resize / scroll ─────────────────────────────────────────
    useEffect(() => {
        if (!isOpen || Platform.OS !== 'web') return;
        const sync = () => updateDropdownPosition();
        window.addEventListener('resize', sync);
        window.addEventListener('scroll', sync, true);
        return () => {
            window.removeEventListener('resize', sync);
            window.removeEventListener('scroll', sync, true);
        };
    }, [isOpen, updateDropdownPosition]);

    const closeDropdown = useCallback(() => {
        setIsOpen(false);
        setSearchText('');
        if (onOpenChange) onOpenChange(false);
    }, [onOpenChange]);

    const handleSelect = useCallback(
        item => {
            onSelect(item.value);   // ← parent state updated immediately
            closeDropdown();
        },
        [onSelect, closeDropdown],
    );

    const getSelectedLabel = () => {
        const selected = data.find(item => item.value === selectedValue);
        return selected ? selected.label : placeholder;
    };

    const toggleDropdown = () => {
        const next = !isOpen;
        if (next) updateDropdownPosition();
        else setSearchText('');
        setIsOpen(next);
        if (onOpenChange) onOpenChange(next);
    };

    // ── Item renderer ──────────────────────────────────────────────────────────
    const renderItem = ({ item }) => (
        <Pressable
            onPress={() => handleSelect(item)}
            style={({ hovered }) => [
                styles.dropdownItem,
                itemStyle,
                selectedValue === item.value && styles.selectedItem,
                hovered && styles.hoverItem,
            ]}
        >
            {({ hovered }) => (
                <Text
                    style={[
                        styles.itemText,
                        itemTextStyle,
                        item.labelStyle,
                        selectedValue === item.value && styles.selectedItemText,
                        hovered && styles.hoverItemText,
                    ]}
                    numberOfLines={2}
                >
                    {item.label}
                </Text>
            )}
        </Pressable>
    );

    const renderEmptyList = () => (
        <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>কোনো ডাটা পাওয়া যায়নি</Text>
        </View>
    );

    // ── Dropdown list JSX ──────────────────────────────────────────────────────
    const dropdownListJSX = (
        <View
            style={[
                styles.dropdownListInner,
                dropdownListStyle,
            ]}
        >
            {shouldShowSearch && (
                <View style={styles.searchContainer}>
                    <TextInput
                        style={styles.searchInput}
                        placeholder={searchPlaceholder}
                        placeholderTextColor="#999"
                        value={searchText}
                        onChangeText={setSearchText}
                        autoFocus={Platform.OS === 'ios' || Platform.OS === 'android'}
                    />
                    {searchText.length > 0 && (
                        <TouchableOpacity
                            style={styles.clearButton}
                            onPress={() => setSearchText('')}
                        >
                            <Text style={styles.clearButtonText}>✕</Text>
                        </TouchableOpacity>
                    )}
                </View>
            )}
            <FlatList
                data={filteredData}
                contentContainerStyle={{ marginBottom: IS_WEB_WIDE ? 30 : 200, marginLeft: 10 }}
                keyExtractor={(item, index) => `${item.value}-${index}`}
                renderItem={renderItem}
                ListEmptyComponent={renderEmptyList}
                showsVerticalScrollIndicator
                keyboardShouldPersistTaps="handled"
                nestedScrollEnabled
                style={[styles.flatList, { maxHeight }]}
                contentContainerStyle={styles.flatListContent}
                initialNumToRender={10}
                maxToRenderPerBatch={20}
                windowSize={5}
            />
        </View>
    );

    // ── Web: wrap in fixed-position div via portal ─────────────────────────────
    const renderDropdownList = () => {
        if (Platform.OS === 'web' && createPortal) {
            const portalStyle = {
                position: 'fixed',
                top: dropdownPosition.openUpward ? 'auto' : dropdownPosition.top,
                bottom: dropdownPosition.openUpward ? dropdownPosition.bottom : 'auto',
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                zIndex: 9999999,
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: 4,
                boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                overflow: 'hidden',
            };

            return createPortal(
                <div style={portalStyle}>
                    {dropdownListJSX}
                </div>,
                getPortalRoot(),
            );
        }

        // Native: classic absolute positioning
        return (
            <View style={[styles.dropdownListNative, { maxHeight }]}>
                {dropdownListJSX}
            </View>
        );
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <View
            ref={containerRef}
            style={[
                styles.container,
                containerStyle,
                { zIndex: isOpen ? zIndex : 1 },
            ]}
        >
            <TouchableOpacity
                style={[styles.dropdownButton, dropdownButtonStyle]}
                onPress={toggleDropdown}
                activeOpacity={0.7}
            >
                <Text
                    style={[
                        styles.selectedText,
                        !selectedValue || selectedValue === ''
                            ? { color: placeholderTextColor }
                            : { color: selectedTextColor },
                    ]}
                    numberOfLines={2}
                >
                    {getSelectedLabel()}
                </Text>
                <Ionicons
                    name={isOpen ? 'chevron-up' : 'chevron-down'}
                    size={18}
                    color="#555"
                />
            </TouchableOpacity>

            {isOpen && renderDropdownList()}
        </View>
    );
};

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
    container: {
        width: '100%',
        position: 'relative',
        // overflow: 'visible',
    },
    dropdownButton: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#000',
        borderRadius: 4,
        backgroundColor: '#FFF',
        paddingHorizontal: 5,
        paddingVertical: Platform.OS === 'web' ? 5 : 10,
        minHeight: 40,
    },
    selectedText: {
        fontSize: 14,
        fontFamily: 'SolaimanLipi',
        flex: 1,
        fontWeight: "500",
        color: '#000',
        lineHeight: 13

    },
    dropdownListNative: {
        position: 'absolute',
        top: '100%',
        left: -10,
        right: -10,
        backgroundColor: '#FFF',
        borderWidth: 1,
        borderColor: '#ccc',
        borderRadius: 4,
        zIndex: 99999,
        elevation: 99999,
        overflow: 'visible',
    },
    dropdownListInner: {
        backgroundColor: '#fff',
    },
    flatList: {
        flexGrow: 0,
    },
    flatListContent: {
        flexGrow: 1,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 5,
        position: 'relative',
        borderBottomWidth: 1,
        borderBottomColor: '#eee',
    },
    searchInput: {
        flex: 1,
        height: 40,
        fontSize: 12,
        fontFamily: 'SolaimanLipi',
        color: '#333',
        paddingRight: 30,
    },
    clearButton: {
        position: 'absolute',
        right: 10,
        padding: 5,
    },
    clearButtonText: {
        fontSize: 14,
        color: '#999',
    },
    dropdownItem: {
        paddingHorizontal: 15,
        paddingVertical: 6,
        justifyContent: 'center',
    },
    selectedItem: {
        backgroundColor: '#f0f4ff',
    },
    selectedItemText: {
        color: '#1a3c8f',
        fontWeight: '600',
    },
    itemText: {
        fontSize: 14,
        fontFamily: 'SolaimanLipi',
        color: '#000',
        // lineHeight: 16,
        fontWeight: "normal"
    },
    hoverItem: {
        backgroundColor: '#007BFF',
    },
    hoverItemText: {
        color: '#FFFFFF',
    },
    emptyContainer: {
        padding: 20,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 100,
    },
    emptyText: {
        fontSize: 12,
        fontFamily: 'SolaimanLipi',
        color: '#999',
        textAlign: 'center',
    },
});

export default CustomDropdown;